import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { AuthUser } from '../types/auth';

export type User = AuthUser;

interface AuthContextType {
  currentUser: User | null;
  token: string | null;
  isLoading: boolean;
  login: (token: string, user: User) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');
    const storedUserJson = localStorage.getItem('currentUser');

    if (storedToken && storedUserJson) {
      try {
        const storedUser: User = JSON.parse(storedUserJson);
        setToken(storedToken);
        setCurrentUser(storedUser);
      } catch (error) {
        console.error("Failed to parse stored user from localStorage:", error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentUser');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (newToken: string, userToLogin: User) => {
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('currentUser', JSON.stringify(userToLogin));
    setToken(newToken);
    setCurrentUser(userToLogin);
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    setToken(null);
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider value={{ currentUser, token, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 